import React, {useState, useEffect} from 'react';
import Modal from '../../../Presentation/components/ui/Modal';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import {AppTitle, AppText, AppCaption, AppSmallText, AppBodyText, AppCardTitle, AppSectionTitle, AppLabelText} from '../../../Presentation/components/ui/Typography';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {
  Package,
  Edit,
  Trash2,
  Plus,
  Image as ImageIcon,
  Tag,
  Warehouse,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin
} from 'lucide-react';
import ProductImagesGallery from './ProductImagesGallery';
import {Product} from '../models/product.model';
import {ProductAPIDataSource} from '../api';
import {mapProductDTOToModel} from '../mappers/product.mapper';
import {InventoryAPIDataSource} from '../../inventory/api/InventoryAPIDataSource';
import {ProductInventoryStatus} from '../../inventory/models/inventory.model';
import {mapProductInventoryStatusDTOToModel} from '../../inventory/mappers/inventory.mapper';
import {useProductOperations} from '../hooks/useProductOperations';
import {LoggerService} from '../../../Services/common/LoggerService';
import {useNotification} from '../../../Services/useNotification';
import AddStockForm from '../../inventory/components/AddStockForm';
import Spinner from '../../../Presentation/components/ui/Spinner';
// import {useNavigate} from 'react-router-dom';
import EditProductModal from './EditProductModal';
import {useAuth} from '../../auth/hooks/useAuth';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import {useProducts} from '../hooks/useProducts';

interface ProductDetailsModalProps {
  productId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  productId,
  isOpen,
  onClose,
  onDeleted
}) => {
  // const navigate = useNavigate();
  const {user} = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [inventoryStatus, setInventoryStatus] = useState<ProductInventoryStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  // Removed stock movements modal per request
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [expandWarehouseSerials, setExpandWarehouseSerials] = useState(false);
  const [expandMarketersSerials, setExpandMarketersSerials] = useState(false);
  // Stores serials removed - stores are customers, not inventory locations

  const {deleteProduct: deleteProductMutation, isDeleting} = useProductOperations();
  const {tags: allTags} = useProducts();
  const {success: showSuccess} = useNotification();

  useEffect(() => {
    if (productId && isOpen) {
      loadProductDetails();
    }
  }, [productId, isOpen]);

  const loadProductDetails = async () => {
    if (!productId) {return;}

    setLoading(true);
    setError(null);

    try {
      const response = await ProductAPIDataSource.getById(productId);
      const productModel = mapProductDTOToModel(response);
      setProduct(productModel);
      await loadInventoryStatus(productId.toString());
    } catch (err) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(err);
      setError(appError.message);
      errorHandlingService.handleError(err); // For notifications/logging
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryStatus = async (productId: string) => {
    setInventoryLoading(true);
    try {
      const response = await InventoryAPIDataSource.getProductInventoryStatus(productId);
      const statusArray = Array.isArray(response) ? response : [response];
      const productStatusDTO = statusArray.find((status: any) => String(status.product_id) === String(productId));

      if (productStatusDTO) {
        const productStatus = mapProductInventoryStatusDTOToModel(productStatusDTO);
        setInventoryStatus(productStatus);
      }
    } catch (err) {
      // Silently handle inventory status errors - not critical for product display
      // Errors are already logged by the API layer
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleAddStock = () => {
    if (!product) {return;}
    setShowAddStockModal(true);
  };

  const handleAddStockSuccess = () => {
    setShowAddStockModal(false);
    if (productId) {
      loadInventoryStatus(productId.toString());
    }
    showSuccess('تم إضافة المخزون بنجاح');
  };


  const handleDelete = async () => {
    if (!product) {return;}

    LoggerService.userAction('Delete Product Initiated', {productId: product.id});

    try {
      await deleteProductMutation.mutateAsync(product.id);
      showSuccess('تم حذف المنتج بنجاح');
      setShowDeleteModal(false);
      onClose();
      onDeleted?.();
    } catch (error) {
      // Error is already handled by deleteProductMutation.onError
      LoggerService.error('Delete product failed', error, 'ProductDetailsModal');
    }
  };

  const handleEdit = () => {
    if (product) {
      setShowEditModal(true);
    }
  };

  const renderProductImages = () => (
    <ProductImagesGallery
      images={product?.images}
      productName={product?.name || ''}
      variant="modal"
      onOpenModal={(index) => { setSelectedImageIndex(index); setShowImageModal(true); }}
    />
  );

  const renderInventorySection = () => {
    if (inventoryLoading) {
      return (
        <div className="flex items-center justify-center py-6">
          <Spinner size={20} />
          <AppBodyText className="ml-2">جاري تحميل بيانات المخزون...</AppBodyText>
        </div>
      );
    }

    if (!inventoryStatus) {
      return (
        <div className="text-center py-6">
          <Warehouse size={32} className="mx-auto text-gray-400 mb-2" />
          <AppBodyText className="text-gray-500">لا توجد بيانات مخزون</AppBodyText>
        </div>
      );
    }

    // Stores removed from calculation - stores are customers, not inventory locations
    const totalStock =
      inventoryStatus.totalQuantity != null
        ? inventoryStatus.totalQuantity
        : (inventoryStatus.warehouseQuantity || 0) + (inventoryStatus.marketersQuantity || 0);
    const stockColor = totalStock === 0 ? 'text-red-600' : totalStock < 10 ? 'text-yellow-600' : 'text-green-600';
    const stockIcon = totalStock === 0 ? XCircle : totalStock < 10 ? AlertCircle : CheckCircle;
    const StockIcon = stockIcon;

    return (
      <div className="space-y-4">
        {/* Total Quantity */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${stockColor.replace('text-', 'bg-').replace('-600', '-100')}`}>
                <StockIcon size={20} className={stockColor} />
              </div>
              <div>
                <AppSmallText className="font-semibold">الكمية</AppSmallText>
                <AppCaption className="text-gray-600">
                  {product?.hasSerial ? 'الأرقام التسلسلية' : 'الكمية'}
                </AppCaption>
              </div>
            </div>
            <div className="text-right">
              <AppTitle className={`${stockColor}`}>
                {totalStock}
              </AppTitle>
              <AppCaption className="text-gray-600">
                {product?.hasSerial ? 'سيريال' : 'قطعة'}
              </AppCaption>
            </div>
          </div>
        </div>

        {/* Summary badges: إخفاء شارة لا يوجد مخزون، وإزالة قسم المواقع حسب الطلب */}
        {inventoryStatus.summary && (
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full font-medium ${
              inventoryStatus.summary.stockStatus?.includes('منخفض')
                ? 'bg-yellow-100 text-yellow-800'
                : inventoryStatus.summary.stockStatus?.includes('غير')
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
            }`}>
              {inventoryStatus.summary.stockStatus}
            </span>
          </div>
        )}

        {/* V2 location chips */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white border rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-brand-600" />
              <AppBodyText>المستودعات</AppBodyText>
            </div>
            <AppBodyText className="font-bold text-brand-600">{inventoryStatus.warehouseQuantity ?? 0}</AppBodyText>
          </div>
          <div className="bg-white border rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-green-600" />
              <AppBodyText>المسوقين</AppBodyText>
            </div>
            <AppBodyText className="font-bold text-green-600">{inventoryStatus.marketersQuantity ?? 0}</AppBodyText>
          </div>
          {/* Stores removed - stores are customers, not inventory locations */}
        </div>

        {/* Serial details (if available) */}
        {inventoryStatus.hasSerial && (inventoryStatus.warehouseDetails?.length || inventoryStatus.marketersDetails?.length) ? (
          <div className="space-y-3">
            {inventoryStatus.warehouseDetails && inventoryStatus.warehouseDetails.length > 0 && (
              <div>
                <AppBodyText className="font-semibold mb-1">سيريالات المستودعات</AppBodyText>
                <div className="flex flex-wrap gap-2">
                  {(expandWarehouseSerials ? inventoryStatus.warehouseDetails : inventoryStatus.warehouseDetails.slice(0, 10)).map((d, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                      {d.serial_number}
                    </span>
                  ))}
                </div>
                {inventoryStatus.warehouseDetails.length > 10 && (
                  <button className="text-brand-600 text-xs mt-2" onClick={() => setExpandWarehouseSerials(v => !v)}>
                    {expandWarehouseSerials ? 'إخفاء' : 'عرض المزيد'}
                  </button>
                )}
              </div>
            )}

            {inventoryStatus.marketersDetails && inventoryStatus.marketersDetails.length > 0 && (
              <div>
                <AppBodyText className="font-semibold mb-1">سيريالات المسوقين</AppBodyText>
                <div className="flex flex-wrap gap-2">
                  {(expandMarketersSerials ? inventoryStatus.marketersDetails : inventoryStatus.marketersDetails.slice(0, 10)).map((d, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                      {d.serial_number}
                    </span>
                  ))}
                </div>
                {inventoryStatus.marketersDetails.length > 10 && (
                  <button className="text-brand-600 text-xs mt-2" onClick={() => setExpandMarketersSerials(v => !v)}>
                    {expandMarketersSerials ? 'إخفاء' : 'عرض المزيد'}
                  </button>
                )}
              </div>
            )}

            {/* Stores serials removed - stores are customers, not inventory locations */}
          </div>
        ) : null}

        {/* Stock Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleAddStock}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2"
          >
            <Plus size={14} className="mr-1" />
            إضافة مخزون
          </Button>
          {/* حركة المخزون: تم إزالتها بناءً على الطلب */}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="تفاصيل المنتج"
        width="max-w-4xl"
      >
        <div className="flex items-center justify-center h-64">
          <Spinner size={40} />
        </div>
      </Modal>
    );
  }

  // Error is handled by StandardizedErrorHandler and shown as toast notification
  if (!product) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="تفاصيل المنتج"
        width="max-w-4xl"
      >
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <AppCardTitle className="text-red-600 mb-2">
            المنتج غير موجود
          </AppCardTitle>
          <Button
            onClick={onClose}
            variant="outline"
            className="border-brand-600 text-brand-600 hover:bg-brand-50"
          >
            إغلاق
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        title={product.name}
        width="max-w-4xl"
      >
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleEdit}
              variant="outline"
              className="border-brand-600 text-brand-600 hover:bg-brand-50"
            >
              <Edit size={16} className="mr-2" />
              تعديل
            </Button>
            <Button
              onClick={() => setShowDeleteModal(true)}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} className="mr-2" />
              حذف
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Product Images */}
            <div>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon size={18} className="text-brand-600" />
                  <AppText className="font-semibold">صور المنتج</AppText>
                </div>
                {renderProductImages()}
              </Card>
            </div>

            {/* Product Details */}
            <div className="space-y-4">
              {/* Basic Information */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package size={18} className="text-brand-600" />
                  <AppText className="font-semibold">المعلومات الأساسية</AppText>
                </div>
                <div className="space-y-3">
                  <div>
                    <AppSmallText className="text-gray-600 mb-1 text-right">اسم المنتج</AppSmallText>
                    <AppText className="font-medium text-right">{product.name}</AppText>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <AppSmallText className="text-gray-600 mb-1 text-right">البراند</AppSmallText>
                      <AppBodyText className="font-medium text-right">{product.brandName}</AppBodyText>
                    </div>
                    <div>
                      <AppSmallText className="text-gray-600 mb-1 text-right">النوع</AppSmallText>
                      <AppBodyText className="font-medium text-right">{product.type || 'غير محدد'}</AppBodyText>
                    </div>
                  </div>
                  <div>
                    <AppSmallText className="text-gray-600 mb-1 text-right">الباركود</AppSmallText>
                    <AppBodyText className="font-mono bg-gray-100 px-2 py-1 rounded text-right">
                      {product.barcode}
                    </AppBodyText>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <AppCaption className="text-gray-600 mb-1 text-right">السعر</AppCaption>
                      <AppText className="font-bold text-green-600 text-right">
                        {formatCurrency(product.price)}
                      </AppText>
                    </div>
                    <div>
                      <AppCaption className="text-gray-600 mb-1 text-right">نوع المخزون</AppCaption>
                      <span className={`px-2 py-1 rounded-full font-medium ${
                        product.hasSerial
                          ? 'bg-brand-100 text-brand-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {product.hasSerial ? 'بسيريال' : 'بدون سيريال'}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div>
                      <AppCaption className="text-gray-600 mb-1 text-right">الوسوم</AppCaption>
                      <div className="flex flex-wrap gap-1">
                        {product.tags.map((tag, index) => {
                          const num = Number(tag);
                          const resolved = !Number.isNaN(num) ? (allTags.find(t => t.id === num)?.name || tag) : tag;
                          return (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full"
                          >
                            <Tag size={10} className="inline mr-1" />
                            {resolved}
                          </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Inventory Section */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Warehouse size={18} className="text-brand-600" />
                  <AppText className="font-semibold">معلومات المخزون</AppText>
                </div>
                {renderInventorySection()}
              </Card>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Stock Modal */}
      <Modal
        open={showAddStockModal}
        onClose={() => setShowAddStockModal(false)}
        title="إضافة مخزون"
        width="max-w-2xl"
      >
        <AddStockForm
          product={product}
          currentUserId={user?.id || 0}
          onSuccess={handleAddStockSuccess}
          onCancel={() => setShowAddStockModal(false)}
        />
      </Modal>

      {/* تم إزالة نافذة حركة المخزون */}

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="تأكيد الحذف"
      >
        <div className="text-center mb-6">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <AppSectionTitle className="mb-2">هل أنت متأكد من حذف هذا المنتج؟</AppSectionTitle>
          <AppText className="font-bold text-red-600">{product.name}</AppText>
          <AppText className="text-gray-600 mt-2">هذا الإجراء لا يمكن التراجع عنه</AppText>
        </div>
        <div className="flex gap-4 justify-center">
          <Button
            variant="ghost"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            إلغاء
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={isDeleting}
          >
            <Trash2 size={16} className="mr-2" />
            حذف المنتج
          </Button>
        </div>
      </Modal>

      {/* Image Modal */}
      <Modal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        title={`صورة ${product.name}`}
        width="max-w-4xl"
      >
        {product.images && product.images.length > 0 && (
          <div className="space-y-4">
            <div className="w-full max-h-96 bg-gray-50 rounded-lg flex items-center justify-center">
              <img
                src={product.images[selectedImageIndex]}
                alt={product.name}
                className="max-h-96 max-w-full object-contain rounded-md"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/assets/images/store_placeholder.png'; }}
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex justify-center gap-2">
                {product.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`w-4 h-1 rounded-sm ${
                      index === selectedImageIndex ? 'bg-brand-600' : 'bg-gray-300'
                    }`}
                    aria-label={`صورة ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Product Modal */}
      {product && (
        <EditProductModal
          isOpen={showEditModal}
          productId={product.id}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};

export default ProductDetailsModal;
